// Notification Utility for GuapGoals
// Supports both Web Push (PWA) and Capacitor native push
// When Capacitor is added, the native methods will take precedence

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check if running in Capacitor
export const isCapacitor = () => {
  return typeof window !== 'undefined' && window.Capacitor !== undefined;
};

// Check if push notifications are supported (web or native)
export const isPushSupported = () => {
  if (isCapacitor()) {
    // Capacitor native push will be available
    return true;
  }
  // Web push check
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Check if notifications are enabled
export const areNotificationsEnabled = () => {
  if (isCapacitor()) {
    // For Capacitor, check will be done via plugin
    // Return true as we'll handle permission in native code
    return true;
  }
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (isCapacitor()) {
    // For Capacitor, permission is handled by the native plugin
    // This will be implemented when Capacitor is added:
    // const { PushNotifications } = await import('@capacitor/push-notifications');
    // const result = await PushNotifications.requestPermissions();
    // return result.receive === 'granted';
    console.log('Capacitor notifications: Permission will be requested via native plugin');
    return true;
  }
  
  // Web push permission
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return false;
  }
  
  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

// Register for push notifications
export const registerForPush = async (token) => {
  if (isCapacitor()) {
    // Capacitor native push registration
    // This will be implemented when Capacitor is added:
    // const { PushNotifications } = await import('@capacitor/push-notifications');
    // await PushNotifications.register();
    // The token will be received via addListener('registration')
    console.log('Capacitor: Native push registration will be handled by plugin');
    return null;
  }
  
  // Web push registration
  return await subscribeToWebPush(token);
};

// Web Push specific - Subscribe to push notifications
const subscribeToWebPush = async (token) => {
  if (!isPushSupported()) {
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Get VAPID public key from server
    const response = await fetch(`${BACKEND_URL}/api/notifications/vapid-key`);
    if (!response.ok) {
      console.error('Failed to get VAPID key');
      return null;
    }
    
    const { public_key } = await response.json();
    
    // Convert VAPID key to Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(public_key);
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey
    });
    
    // Send subscription to backend
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const subscribeResponse = await fetch(`${BACKEND_URL}/api/notifications/subscribe`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(subscription.toJSON())
    });
    
    if (subscribeResponse.ok) {
      console.log('Successfully subscribed to push notifications');
      return subscription;
    }
    
    return null;
  } catch (error) {
    console.error('Error subscribing to push:', error);
    return null;
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async (token) => {
  if (isCapacitor()) {
    // Capacitor unsubscribe
    console.log('Capacitor: Unsubscribe will be handled by native plugin');
    return true;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      
      // Notify backend
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      await fetch(`${BACKEND_URL}/api/notifications/unsubscribe`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error unsubscribing from push:', error);
    return false;
  }
};

// Update notification settings
export const updateNotificationSettings = async (settings, token) => {
  try {
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${BACKEND_URL}/api/notifications/settings`, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: JSON.stringify(settings)
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error updating notification settings:', error);
    return false;
  }
};

// Get notification settings
export const getNotificationSettings = async (token) => {
  try {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${BACKEND_URL}/api/notifications/settings`, {
      headers,
      credentials: 'include'
    });
    
    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    console.error('Error getting notification settings:', error);
    return null;
  }
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Service worker registration (for web push)
export const registerServiceWorker = async () => {
  if (isCapacitor()) {
    // Capacitor doesn't need a service worker for push
    console.log('Capacitor: Service worker not needed for native push');
    return null;
  }
  
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    console.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

// Send test notification
export const sendTestNotification = async (token) => {
  try {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${BACKEND_URL}/api/notifications/test`, {
      method: 'POST',
      headers,
      credentials: 'include'
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error sending test notification:', error);
    return false;
  }
};

// Update user timezone
export const updateUserTimezone = async (token) => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${BACKEND_URL}/api/notifications/timezone`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ timezone })
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error updating timezone:', error);
    return false;
  }
};

// Subscribe to push notifications (alias for registerForPush for backward compatibility)
export const subscribeToPush = async (token) => {
  return await registerForPush(token);
};

// Get push subscription
export const getPushSubscription = async () => {
  if (isCapacitor() || !('serviceWorker' in navigator)) {
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (error) {
    console.error('Error getting push subscription:', error);
    return null;
  }
};

// Show local notification
export const showLocalNotification = async (title, options = {}) => {
  if (isCapacitor()) {
    // For Capacitor, use LocalNotifications plugin
    // const { LocalNotifications } = await import('@capacitor/local-notifications');
    // await LocalNotifications.schedule({ notifications: [{ title, body: options.body, id: Date.now() }] });
    console.log('Capacitor: Local notification would be shown via native plugin');
    return true;
  }
  
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return false;
  }
  
  try {
    const notification = new Notification(title, {
      icon: '/guap-icon-192.png',
      badge: '/guap-icon-192.png',
      ...options
    });
    return notification;
  } catch (error) {
    console.error('Error showing notification:', error);
    return false;
  }
};

/*
 * CAPACITOR NATIVE PUSH IMPLEMENTATION GUIDE
 * ==========================================
 * When you're ready to add Capacitor native push:
 * 
 * 1. Install the plugin:
 *    npx cap add ios
 *    npx cap add android
 *    npm install @capacitor/push-notifications
 * 
 * 2. Configure for iOS (AppDelegate.swift):
 *    - Enable Push Notifications capability in Xcode
 *    - Add APNs key to Apple Developer account
 * 
 * 3. Configure for Android (MainActivity.java):
 *    - Add google-services.json from Firebase
 *    - Configure Firebase Cloud Messaging
 * 
 * 4. Initialize in your app:
 *    import { PushNotifications } from '@capacitor/push-notifications';
 * 
 *    PushNotifications.addListener('registration', token => {
 *      // Send token to backend: POST /api/devices/register { token, platform }
 *    });
 * 
 *    PushNotifications.addListener('pushNotificationReceived', notification => {
 *      // Handle foreground notification
 *    });
 * 
 *    PushNotifications.addListener('pushNotificationActionPerformed', action => {
 *      // Handle notification tap
 *    });
 * 
 * 5. Backend changes needed:
 *    - Create /api/devices/register endpoint to store FCM/APN tokens
 *    - Use Firebase Admin SDK (Android) and APNs (iOS) to send pushes
 */

export default {
  isCapacitor,
  isPushSupported,
  areNotificationsEnabled,
  requestNotificationPermission,
  registerForPush,
  unsubscribeFromPush,
  updateNotificationSettings,
  getNotificationSettings,
  registerServiceWorker,
  sendTestNotification
};
